import {
  Box,
  Typography,
  Container,
  Grid,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Link,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useEffect, useRef, useState } from 'react';
import bgImage from './assets/header-bg.png';
import ddLogoImage from './assets/dd-logo.png';
import generatePDF from 'react-to-pdf';
import BarChart from './components/bar-chart';
import graphDataJson from './graphData.json';

const focusAreaChipClasses = [
  {
    backgroundColor: '#62B5E5',
    color: '#fff',
    textTransform: 'uppercase',
  },
  {
    backgroundColor: '#005587',
    color: '#fff',
  },
  {
    backgroundColor: '#046A38',
    color: '#fff',
  },
];

interface OverallAggregatedScore {
  overall_aggregated_score: number;
}

interface AdjustedScores {
  data_maturity: number;
  enterprise_readiness: number;
  strategy_and_governance: number;
  technology_and_tooling: number;
}

type ScoreLabel = keyof AdjustedScores;

interface ScoringLabels {
  overall: string;
  data_maturity: string;
  enterprise_readiness: string;
  strategy_and_governance: string;
  technology_and_tooling: string;
}

interface CalculatedScores {
  adjustedScoresPercent: AdjustedScores;
  aggregatedScore: OverallAggregatedScore;
}

interface CalculatedScoresItem {
  data: string;
  field: string;
  questionId: string;
}

interface QuestionFieldOption {
  __typename: string;
  id: string;
  key: null;
  title: string;
  value: string;
}

interface QuestionField {
  __typename: string;
  id: string;
  title: string;
  slug: null;
  type: string;
  description: string;
  required: boolean;
  defaultValue: null;
  logic: any[];
  options: QuestionFieldOption[];
  rating: {
    __typename: string;
    steps: null;
    shape: null;
  };
}

interface graphDataJsonField {
  id: string;
  data: number[];
}

interface UserAnswer {
  field?: string;
  questionId?: string;
  data: string;
}

interface QuestionOption {
  id: string;
  key: any;
  title: string;
  value: string;
  __typename: string;
}

interface Question {
  defaultValue: any;
  description: string;
  id: string;
  logic: any;
  options: QuestionOption[];
}

interface GraphDataItem {
  id: string;
  description: string;
  optionLabels: string[];
  optionText: string;
  title: string;
}

const getAnswerByQuestionId = (
  answers: any,
  questionId: string
): CalculatedScoresItem => {
  const filteredAnswer = answers.filter(
    (item: any) => item.questionId === questionId || item.field === questionId
  );
  return filteredAnswer[0];
};

const getWeights = (answers: any): AdjustedScores => {
  const question7Value: Number = Number(
    removeDoubleQuotes(getAnswerByQuestionId(answers, 'NjrbJj').data)
  );
  if (question7Value === 1) {
    return {
      technology_and_tooling: 0.3,
      enterprise_readiness: 0.4,
      strategy_and_governance: 0.2,
      data_maturity: 0.1,
    };
  } else if (question7Value === 2) {
    return {
      technology_and_tooling: 0.25,
      enterprise_readiness: 0.25,
      strategy_and_governance: 0.25,
      data_maturity: 0.25,
    };
  } else {
    return {
      technology_and_tooling: 0.2,
      enterprise_readiness: 0.2,
      strategy_and_governance: 0.3,
      data_maturity: 0.3,
    };
  }
};

const answerValue = (score: number): number => {
  if (score <= 2) {
    return 1;
  }
  return score - 1;
};

const getNumberOfOptions = (
  options: [QuestionField]
): Record<string, number> => {
  const returbObj = {
    Q11: getQuestionById(options, '8VP0eV').options.length - 1,
    Q12: getQuestionById(options, 'XjbkRV').options.length - 1,
    Q13: getQuestionById(options, 'G74G01').options.length - 1,
    Q14: getQuestionById(options, 'qVEvG1').options.length - 1,
    Q15: getQuestionById(options, 'ejKmw1').options.length - 1,
    Q16: getQuestionById(options, 'X7qEP7').options.length - 1,
    Q17: getQuestionById(options, 'wVJ28V').options.length - 1,
    Q18: getQuestionById(options, '516G57').options.length - 1,
    Q19: getQuestionById(options, 'NjGWQj').options.length - 1,
    Q20: getQuestionById(options, 'd1d987').options.length - 1,
    Q21: getQuestionById(options, 'L7Lw21').options.length - 1,
    Q22: getQuestionById(options, 'G7RrR7').options.length - 1,
    Q23: getQuestionById(options, 'NjrNJj').options.length - 1,
  };
  return returbObj;
};

const prepareAnswers = (answers: any): any => {
  const readinessData: Record<string, Record<string, number>> = {
    technology_and_tooling: {
      Q11: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, '8VP0eV').data)
        )
      ),
      Q12: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'XjbkRV').data)
        )
      ),
      Q13: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'G74G01').data)
        )
      ),
      Q14: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'qVEvG1').data)
        )
      ),
      Q15: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'ejKmw1').data)
        )
      ),
    },
    enterprise_readiness: {
      Q19: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'NjGWQj').data)
        )
      ),
      Q20: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'd1d987').data)
        )
      ),
      Q22: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'G7RrR7').data)
        )
      ),
    },
    data_maturity: {
      Q16: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'X7qEP7').data)
        )
      ),
      Q17: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'wVJ28V').data)
        )
      ),
      Q18: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, '516G57').data)
        )
      ),
    },
    strategy_and_governance: {
      Q21: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'L7Lw21').data)
        )
      ),
      Q23: answerValue(
        Number(
          removeDoubleQuotes(getAnswerByQuestionId(answers, 'NjrNJj').data)
        )
      ),
    },
  };
  return readinessData;
};

const removeDoubleQuotes = (str: string) => {
  return str.split('"').join('');
};

const calculateOverallScore = (
  options: any,
  userAnswers: any
): Record<string, number> => {
  const overallScores: Record<string, number> = {};
  const responses = prepareAnswers(userAnswers);
  const maxValues = getNumberOfOptions(options);

  const weights: any = getWeights(userAnswers);
  for (const area in responses) {
    let totalScore = 0;
    let totalResponses = 0;
    for (const question in responses[area]) {
      const responseScore = responses[area][question];
      const numOptions = maxValues[question];
      const normalizedScore = responseScore / numOptions;
      totalScore += normalizedScore;
      totalResponses += 1;
    }
    const areaScore = totalScore / totalResponses;
    overallScores[area] = areaScore * weights[area];
  }
  return overallScores;
};

const showScores = (options: any, userAnswers: any): CalculatedScores => {
  const overallScores = calculateOverallScore(options, userAnswers);
  const aggregatedScore =
    Object.values(overallScores).reduce((acc, score) => acc + score, 0) * 100;
  const aggregatedScoreObj: OverallAggregatedScore = {
    overall_aggregated_score: aggregatedScore,
  };

  const weights: any = getWeights(userAnswers);

  const adjustedScores: any = {};
  for (const category in overallScores) {
    adjustedScores[category] = overallScores[category] / weights[category];
  }

  const adjustedScoresPercent: any = {};
  for (const category in adjustedScores) {
    adjustedScoresPercent[category] = adjustedScores[category] * 100;
  }

  return { aggregatedScore: aggregatedScoreObj, adjustedScoresPercent };
};

const getQuestionById = (questions: any, questionId: string): QuestionField => {
  const filteredAnswer = questions.filter(
    (item: { id: string }) => item.id === questionId
  );
  return filteredAnswer[0];
};

const getCategoryScoreLabel = (score: number): string => {
  if (score >= 75) {
    return 'Strong';
  } else if (score >= 50) {
    return 'Moderate';
  } else {
    return 'Lacking';
  }
};

const getScoringLabels = (scores: CalculatedScores): ScoringLabels => {
  return {
    overall: getCategoryScoreLabel(
      scores.aggregatedScore.overall_aggregated_score
    ),
    data_maturity: getCategoryScoreLabel(
      scores.adjustedScoresPercent.data_maturity
    ),
    enterprise_readiness: getCategoryScoreLabel(
      scores.adjustedScoresPercent.enterprise_readiness
    ),
    strategy_and_governance: getCategoryScoreLabel(
      scores.adjustedScoresPercent.strategy_and_governance
    ),
    technology_and_tooling: getCategoryScoreLabel(
      scores.adjustedScoresPercent.technology_and_tooling
    ),
  };
};

const getLowestCapabilityScoreLabel = (scores: any): string => {
  const lowestCapabilityScore: ScoreLabel = Object.keys(scores).reduce(
    (acc: any, key: any) => {
      return scores[key] < scores[acc] ? key : acc;
    },
    Object.keys(scores)[0] as ScoreLabel
  );

  if (lowestCapabilityScore === 'technology_and_tooling') {
    return 'Technology & Tooling';
  } else if (lowestCapabilityScore === 'enterprise_readiness') {
    return 'Enterprise Readiness';
  } else if (lowestCapabilityScore === 'data_maturity') {
    return 'Data & Analytics';
  } else {
    return 'Strategy & Governance';
  }
};

const getSuggestedImprovementAreas = (scores: CalculatedScores): string => {
  let strArr = [];
  for (const [key, value] of Object.entries(scores.adjustedScoresPercent)) {
    if (key === 'technology_and_tooling' && value < 65) {
      strArr.push('Technology & Tooling');
    }
    if (key === 'enterprise_readiness' && value < 65) {
      strArr.push('Enterprise Readiness');
    }
    if (key === 'data_maturity' && value < 65) {
      strArr.push('Data & Analytics');
    }
    if (key === 'strategy_and_governance' && value < 65) {
      strArr.push('Strategy & Governance');
    }
  }

  if (strArr.length) {
    return strArr.join(', ');
  } else {
    return getLowestCapabilityScoreLabel(scores.adjustedScoresPercent);
  }
};

const getFocusAreaChipClass = (data: any, type: string) => {
  if (data[type] === 'Strong') {
    return 2;
  } else if (data[type] === 'Moderate') {
    return 1;
  } else {
    return 0;
  }
};

const getQuestionIdsForResults = (answers: any, jsonData: any) => {
  const updated = answers.map((item: any, i: number) => {
    if (!item.questionId) {
      return { questionId: jsonData.questions[i].questionId, ...item };
    } else {
      return item;
    }
  });
  return updated;
};

const getOverallStatement = (scoreLabel: string) => {
  const options = [
    'There are some critical foundational steps your organization still needs to take in order to be fully prepared to adopt and use genAI for marketing successfully',
    'You have some foundational capabilities in place, but there are still a few gaps you need to fill before launching a successful genAI program within your marketing organization',
    "You've laid a strong foundation and we feel you are ready to successfully launch and scale a genAI program for your marketing organization",
  ];
  if (scoreLabel === 'Strong') {
    return options[2];
  } else if (scoreLabel === 'Moderate') {
    return options[1];
  } else {
    return options[0];
  }
};

const formatGraphData = (
  graphData: any,
  answers: any,
  questions: any
): GraphDataItem[] => {
  const formattedData = graphData.map((item: graphDataJsonField) => {
    const filteredAnswer = answers.find(
      (answer: UserAnswer) => (answer?.questionId || answer?.field) === item.id
    );
    const filteredQuestions = questions.find(
      (question: Question) => question.id === item.id
    );
    const optionLabels = filteredQuestions.options
      .filter((option: QuestionOption) => {
        // We have to filter out the I'm not sure option to remove it from the graph
        if (option.title !== "I'm not sure") {
          return option.title;
        }
      })
      .map((item: QuestionOption) => item.title);
    const selectedOption = filteredQuestions.options.filter(
      (option: QuestionOption) =>
        option.value === removeDoubleQuotes(filteredAnswer.data)
    );
    return {
      ...item,
      title: filteredQuestions.title,
      description: filteredQuestions.description,
      optionText: selectedOption[0].title || "I'm not sure",
      optionLabels: optionLabels,
    };
  });
  return formattedData;
};

const GenAiReadiness = ({ design, questions, userAnswers, jsonData }: any) => {
  const [calculatedScores, setCalculatedScores] = useState<CalculatedScores>();
  const [scoringLabels, setScoringLabels] = useState<ScoringLabels>();
  const [selectedUseCase, setSelectedUseCase] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [selectedDevelopmentPattern, setSelectedDevelopmentPattern] =
    useState<string>('');
  const [improvementAreas, setImprovementAreas] = useState<string>();
  const [overallStatement, setOverallStatement] = useState<string>();
  const [graphData, setGraphData] = useState<GraphDataItem[]>([]);
  const [loading, setLoading] = useState(false);
  const targetRef = useRef<HTMLDivElement>(null);

  const handleGeneratePDF = async () => {
    setLoading(true);
    await generatePDF(targetRef, {
      filename: 'GenAI-Readiness-Calculator.pdf',
    });
    setLoading(false);
  };

  useEffect(() => {
    const answers = getQuestionIdsForResults(userAnswers, jsonData);

    if (answers.length) {
      const scoresFromCalc = showScores(questions, answers);
      const firstName = removeDoubleQuotes(
        getAnswerByQuestionId(answers, 'g1o8Y7').data
      );
      setCalculatedScores(scoresFromCalc);
      setScoringLabels(getScoringLabels(scoresFromCalc));
      setImprovementAreas(getSuggestedImprovementAreas(scoresFromCalc));
      setFirstName(firstName);
    }
    if (questions.length && answers.length) {
      const question3 = getQuestionById(questions, 'w1ekQj');
      const question3Value = Number(
        removeDoubleQuotes(getAnswerByQuestionId(answers, 'w1ekQj').data)
      );

      if (question3?.options[question3Value - 1]?.title) {
        setSelectedUseCase(question3?.options[question3Value - 1].title);
      }

      const question7: any = getQuestionById(questions, 'NjrbJj');

      const question7Value = Number(
        removeDoubleQuotes(getAnswerByQuestionId(answers, 'NjrbJj').data)
      );

      if (question7Value && question7?.options[question7Value - 1]) {
        setSelectedDevelopmentPattern(
          question7?.options[question7Value - 1].title
        );
      }
    }
  }, [userAnswers, questions, jsonData]);

  useEffect(() => {
    if (scoringLabels) {
      setOverallStatement(getOverallStatement(scoringLabels?.overall));
    }
  }, [scoringLabels]);

  useEffect(() => {
    if (graphDataJson && userAnswers.length && questions) {
      const formattedData = formatGraphData(
        graphDataJson.fields,
        userAnswers,
        questions
      );
      setGraphData(formattedData);
    }
  }, [graphDataJson, userAnswers, questions]);

  return (
    <>
      <div
        className='main-end-page-wrapper'
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Container ref={targetRef}>
          {loading && (
            <Backdrop
              open={loading}
              style={{ zIndex: 999, height: '100%', position: 'absolute' }}
            >
              <CircularProgress
                sx={{ position: 'absolute', top: '75px', color: '#fff' }}
              />
            </Backdrop>
          )}
          <Box
            sx={{
              backgroundImage: `url(${bgImage})`,
              backgroundSize: 'cover',
              width: '100%',
              height: '450px',
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
              position: 'relative',
              justifyContent: 'center',
            }}
          >
            <IconButton
              sx={{
                color: '#fff',
                position: 'absolute',
                top: '10px',
                right: '10px',
              }}
              onClick={handleGeneratePDF}
            >
              <Typography
                sx={{
                  fontSize: '1rem',
                  color: '#fff',
                  display: 'inline-block',
                  textDecoration: 'underline',
                  whiteSpace: 'pre-line',
                  marginRight: '2px',
                }}
              >
                Download Report
              </Typography>
              <FileDownloadIcon
                sx={{
                  width: '21px',
                  height: '21px',
                  mt: '1px',
                }}
              />
            </IconButton>
            <Box
              sx={{
                position: 'absolute',
                top: '20px',
                left: '20px',
              }}
            >
              <img src={ddLogoImage} style={{ width: '100px' }} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Open Sans ExtraBold',
                  fontSize: '60px',
                  fontStyle: 'italic',
                  color: '#fff',
                  textTransform: 'uppercase',
                  fontWeight: '800',
                }}
              >
                Readiness Score
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Open Sans ExtraBold',
                  fontSize: '110px',
                  lineHeight: '85px',
                  fontStyle: 'italic',
                  color: 'transparent',
                  WebkitTextStrokeWidth: '3px',
                  WebkitTextStrokeColor: '#fff',
                }}
              >
                {calculatedScores
                  ? Math.floor(
                      calculatedScores.aggregatedScore.overall_aggregated_score
                    )
                  : 0}
              </Typography>
            </Box>
          </Box>

          <Typography
            color={design.colors.question}
            align='center'
            sx={{
              marginTop: '30px',
              fontFamily: 'Open Sans',
              fontWeight: '700',
              fontSize: '24px',
            }}
          >
            Thank you for completing the GenAI Readiness Assessment, {firstName}
            .
          </Typography>
          {calculatedScores && scoringLabels && (
            <>
              <Typography
                color={design.colors.question}
                sx={{
                  marginTop: '0px',
                  fontFamily: 'Open Sans',
                  fontSize: '18px',
                  lineHeight: '28px',
                  fontWeight: '300',
                  mb: 4,
                  paddingTop: '30px',
                }}
              >
                Your genAI ambitions are focused on{' '}
                {selectedUseCase.toLowerCase()} by{' '}
                {selectedDevelopmentPattern.toLowerCase()}. Based on your
                responses, we&#39;ve calculated your score to be{' '}
                {Math.floor(
                  calculatedScores.aggregatedScore.overall_aggregated_score
                )}{' '}
                (out of 100). {overallStatement}. However, there are several
                opportunities to upscale your maturity. We can help you maximize
                the impact of further investment to mature your genAI capability
                by focusing on the the following areas:{' '}
                {improvementAreas ? improvementAreas : ''}
              </Typography>
              <Grid container columns={12} sx={{ mb: 4 }}>
                <Grid item xs={12} md={3} sx={{ padding: '10px' }}>
                  <Typography
                    sx={{
                      ...focusAreaChipClasses[
                        getFocusAreaChipClass(
                          scoringLabels,
                          'technology_and_tooling'
                        )
                      ],
                      fontSize: '14px',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '130px',
                      height: '30px',
                      padding: '20px 0 20px',
                      letterSpacing: '2.2px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {scoringLabels?.technology_and_tooling}
                  </Typography>

                  <Box
                    sx={{
                      borderBottom: 'solid 1px #D9D9D9',
                      pb: 2,
                    }}
                  >
                    <Typography
                      color={design.colors.question}
                      sx={{
                        marginTop: '20px',
                        fontFamily: 'Open Sans',
                        fontWeight: '300',
                        fontSize: '36px',
                        lineHeight: '50px',
                      }}
                    >
                      Technology & Tooling
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      color: 'black',
                      paddingTop: 2,
                      background: 'rgba(255, 255, 255, 0.95)',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        marginBottom: '10px',
                        marginTop: '10px',
                      }}
                    >
                      Not all platforms are “AI-Ready” out of the box, and not
                      all technologies can support the heavy data needs and
                      processing complexities of a genAI project at scale.
                      Understanding how ready your existing technology
                      infrastructure is for genAI is critical to mitigating
                      compatibility challenges and potential security concerns.
                      To be genAI-ready, consider the following:
                    </Typography>
                    <List sx={{ listStyleType: 'disc' }}>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Clearly identify the compatibility of your current systems with the processing needs and data volume of your planned genAI initiative. '
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Have a plan to address the new cyber and privacy risks and requirements of AI'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Resolve technical debt and update technology infrastructure to prepare for the speed and scale required.'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Implement the right quality control tools to measure, monitor, and optimize your genAI systems to prevent hallucination, loss of context, and other inappropriate outputs.'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                    </List>
                    {scoringLabels.technology_and_tooling === 'Strong' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        You&#39;ve done the work to ensure your technology can
                        successfully support a genAI initiative. Plan now for
                        the next wave of technology transformation by staying on
                        top of emerging technology and cyber risks/requirements.
                      </Typography>
                    )}
                    {scoringLabels.technology_and_tooling === 'Moderate' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        You have some of the right infrastructure in place to
                        support your genAI ambitions, but the gaps could leave
                        you vulnerable as you grow. Clean up your technical debt
                        and be sure you have invested in the right set of
                        platforms to implement, operate, and scale your genAI
                        ambitions.
                      </Typography>
                    )}
                    {scoringLabels.technology_and_tooling === 'Lacking' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        Before you launch a genAI project you will need to make
                        some key updates to your enterprise technology to ensure
                        you can execute your ambitions in a secure and scalable
                        way. Modernize your marketing technology stack to help
                        ensure your genAI project has the right support to
                        succeed.
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} sx={{ padding: '10px' }}>
                  <Typography
                    sx={{
                      ...focusAreaChipClasses[
                        getFocusAreaChipClass(
                          scoringLabels,
                          'enterprise_readiness'
                        )
                      ],
                      fontSize: '14px',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '130px',
                      height: '30px',
                      padding: '20px 0 20px',
                      letterSpacing: '2.2px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {scoringLabels?.enterprise_readiness}
                  </Typography>

                  <Box
                    sx={{
                      borderBottom: 'solid 1px #D9D9D9',
                      pb: 2,
                    }}
                  >
                    <Typography
                      color={design.colors.question}
                      sx={{
                        marginTop: '20px',
                        fontFamily: 'Open Sans',
                        fontWeight: '300',
                        fontSize: '36px',
                        lineHeight: '50px',
                      }}
                    >
                      Enterprise Readiness
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      color: 'black',
                      paddingTop: 2,
                      background: '#fff',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        marginBottom: '10px',
                        marginTop: '10px',
                      }}
                    >
                      The true potential of genAI is harnessed when it is
                      successfully integrated with other technologies and
                      business processes and thoroughly embraced and adopted by
                      employees, leaders, and customers alike. To get
                      genAI-ready, consider the following:
                    </Typography>
                    <List sx={{ listStyleType: 'disc' }}>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Ensure you have secured the right leadership buy-in and budget resources to test, develop and scale your project.'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Identify what needed skills are missing from your talent pool and plan to upskill or outsource those required capabilities.'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary="Draft a change management plan to prepare your teams for this technology's new processes, risks and benefits."
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Consider carefully how your new program will integrate into all the other business functions within and across the enterprise.'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                    </List>
                    {scoringLabels.enterprise_readiness === 'Strong' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        Your organization has implemented a strong foundation of
                        processes and talent to implement and operate your genAI
                        strategy. Implementing the right feedback mechanisms for
                        workforce and customers will help ensure you catch
                        emerging weak spots before they endanger your project.
                      </Typography>
                    )}
                    {scoringLabels.enterprise_readiness === 'Moderate' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        You have some of the right capabilities to help manage
                        the change that genAI will bring. A change management
                        plan and roadmap can help you identify weak spots where
                        new capacities may be needed and help ensure you have
                        budgeted the right time and resources to execute your
                        ambitions fully.
                      </Typography>
                    )}
                    {scoringLabels.enterprise_readiness === 'Lacking' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        Your project needs a firm foundation to succeed. Before
                        investing heavily in a genAI pilot, be sure you clearly
                        understand how your proposed project will affect all
                        parts of your organization from front line to back
                        office. Consider filling in the gaps in your core
                        capabilities through in-housing, outsourcing, or
                        strategic partnerships that can help you rapidly scale
                        while you build your teams&#39; capacity and expertise.
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} sx={{ padding: '10px' }}>
                  <Typography
                    sx={{
                      ...focusAreaChipClasses[
                        getFocusAreaChipClass(scoringLabels, 'data_maturity')
                      ],
                      fontSize: '14px',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '130px',
                      height: '30px',
                      padding: '20px 0 20px',
                      letterSpacing: '2.2px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {scoringLabels?.data_maturity}
                  </Typography>
                  <Box
                    sx={{
                      borderBottom: 'solid 1px #D9D9D9',
                      pb: 2,
                    }}
                  >
                    <Typography
                      color={design.colors.question}
                      sx={{
                        marginTop: '20px',
                        fontFamily: 'Open Sans',
                        fontWeight: '300',
                        fontSize: '36px',
                        lineHeight: '50px',
                      }}
                    >
                      Data & Analytics
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      color: 'black',
                      paddingTop: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        marginBottom: '10px',
                        marginTop: '10px',
                      }}
                    >
                      Successful genAI projects rely on large amounts of data to
                      work their magic. To be useful, that data must be
                      trustworthy, secure, accessible, and organized so that
                      your genAI tool can successfully interpret for meaningful
                      insights and outputs. To get genAI-ready, consider the
                      following:
                    </Typography>
                    <List sx={{ listStyleType: 'disc' }}>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Map out where the data needed for your project currently resides and eliminate silos or manual processes that may slow or prevent access to the needed data for your project.'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Implement the right privacy and security protections on your data systems to avoid breaches of customer trust and ensure regulatory compliance.'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Design suitable data hygiene protocols to ensure the data you collect is accurate, consistent, and relevant to your project goals.'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                      <ListItem
                        sx={{
                          marginBottom: '10px',
                          marginTop: '10px',
                          display: 'list-item',
                          padding: '0px',
                        }}
                      >
                        <ListItemText
                          primaryTypographyProps={{ fontSize: '13px' }}
                          primary='Create the right analytics and measurement tools to monitor, measure, and adjust as needed.'
                          sx={{
                            marginTop: '10px',
                          }}
                        />
                      </ListItem>
                    </List>
                    {scoringLabels.data_maturity === 'Strong' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        Your data infrastructure is integrated, well organized,
                        and ready for implementing a genAI project. Plan now for
                        the potential growth in data storage and support needs
                        as your project scales.
                      </Typography>
                    )}
                    {scoringLabels.data_maturity === 'Moderate' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        You have some of the right data infrastructure in place,
                        but your project may get stalled as your need for
                        accurate data increases. Fix the gaps in your customer
                        data infrastructure to help ensure your genAI outputs.
                      </Typography>
                    )}
                    {scoringLabels.data_maturity === 'Lacking' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        Before you launch your genAI ambitions, you will want to
                        clearly map what data you will need and where it is
                        currently stored within your organization. A single
                        customer data platform can provide the right foundation
                        to collect, clean, organize, and shape your data into a
                        format that can be dynamically used by your genAI
                        project.
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={3} sx={{ padding: '10px' }}>
                  <Typography
                    sx={{
                      ...focusAreaChipClasses[
                        getFocusAreaChipClass(
                          scoringLabels,
                          'strategy_and_governance'
                        )
                      ],
                      fontSize: '14px',
                      borderRadius: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '130px',
                      height: '30px',
                      padding: '20px 0 20px',
                      letterSpacing: '2.2px',
                      textTransform: 'uppercase',
                    }}
                  >
                    {scoringLabels?.strategy_and_governance}
                  </Typography>
                  <Box
                    sx={{
                      borderBottom: 'solid 1px #D9D9D9',
                      pb: 2,
                    }}
                  >
                    <Typography
                      color={design.colors.question}
                      sx={{
                        marginTop: '20px',
                        fontFamily: 'Open Sans',
                        fontWeight: '300',
                        fontSize: '36px',
                        lineHeight: '50px',
                      }}
                    >
                      Strategy & Governance
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      color: 'black',
                      paddingTop: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        marginBottom: '10px',
                        marginTop: '10px',
                      }}
                    >
                      Trustworthiness and effectiveness are two of the most
                      critical factors in determining whether your genAI project
                      will create an impact that matters to your bottom line. To
                      be genAI-ready you should consider the following:
                      <List sx={{ listStyleType: 'disc' }}>
                        <ListItem
                          sx={{
                            marginBottom: '10px',
                            marginTop: '10px',
                            display: 'list-item',
                            padding: '0px',
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '13px' }}
                            primary='Be sure you fully understand your project&#39;s legal
                          and cyber risk profile.'
                            sx={{
                              marginTop: '10px',
                            }}
                          />
                        </ListItem>
                        <ListItem
                          sx={{
                            marginBottom: '10px',
                            marginTop: '10px',
                            display: 'list-item',
                            padding: '0px',
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '13px' }}
                            primary='Align your teams on Human in the Loop governance
                            standards.'
                            sx={{
                              marginTop: '10px',
                            }}
                          />
                        </ListItem>
                        <ListItem
                          sx={{
                            marginBottom: '10px',
                            marginTop: '10px',
                            display: 'list-item',
                            padding: '0px',
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '13px' }}
                            primary='Create a plan for ensuring ethical and responsible use
                            of AI and a strategy for preventing biased,
                            inaccurate, or inappropriate outputs from your
                            project.'
                            sx={{
                              marginTop: '10px',
                            }}
                          />
                        </ListItem>
                        <ListItem
                          sx={{
                            marginBottom: '10px',
                            marginTop: '10px',
                            display: 'list-item',
                            padding: '0px',
                          }}
                        >
                          <ListItemText
                            primaryTypographyProps={{ fontSize: '13px' }}
                            primary='Identify the highest value, lowest risk use cases for
                            your organization.'
                            sx={{
                              marginTop: '10px',
                            }}
                          />
                        </ListItem>
                      </List>
                    </Typography>

                    {scoringLabels.strategy_and_governance === 'Strong' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        You have clear governance structures in place to ensure
                        this new technology is deployed safely and in alignment
                        with your company&#39;s legal and risk profile and brand
                        reputation. You&#39;ve identified a use case that solves
                        a key pain point for your organization in a way that
                        will build trust with both your customers and your
                        workforce. Maintaining a Human in the Loop governance
                        for new features as they are developed will help ensure
                        you can spot and mitigate new risks (and capitalize on
                        new opportunities) as they emerge.
                      </Typography>
                    )}
                    {scoringLabels.strategy_and_governance === 'Moderate' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        You have some of the right data infrastructure in place,
                        but your project may get stalled as your need for
                        accurate data increases. Fix the gaps in your customer
                        data infrastructure to ensure your genAI outputs&#39;
                        stability, safety, and accuracy.
                      </Typography>
                    )}
                    {scoringLabels.strategy_and_governance === 'Lacking' && (
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          marginBottom: '10px',
                          marginTop: '10px',
                        }}
                      >
                        Before you launch your genAI ambitions, you will want to
                        clearly map the data you need and where it is currently
                        stored within your organization. A single customer data
                        platform can provide the right foundation to collect,
                        clean, organize, and shape your data into a format that
                        your genAI project can dynamically use.
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </>
          )}
          {graphData.length > 0 && (
            <>
              <Typography
                sx={{
                  fontSize: '2.25rem',
                  marginBottom: '10px',
                  marginTop: '10px',
                  fontStyle: 'italic',
                  textTransform: 'uppercase',
                  fontWeight: '700',
                  textAlign: 'center',
                }}
              >
                How do you compare to your peers?
              </Typography>
              <Typography
                sx={{
                  fontSize: '1.5rem',

                  textAlign: 'center',
                  padding: '10px',
                }}
              >
                See how your answers compare to the sample group of participants
                that have been surveyed for this questionnaire. The highlighted
                numbers show your selected answers; how do you measure up?
              </Typography>
              <Box sx={{ mt: 5 }}>
                {graphData.map((item) => (
                  <BarChart key={item.id} data={item} chartType='horizontal' />
                ))}
              </Box>
              <Box sx={{ p: '40px 40px 40px 0' }}>
                <Link
                  sx={{
                    padding: '40px 0px',
                    fontWeight: 'bold',
                    fontSize: '1.5rem',
                    mt: '200px',
                    color: '#000',
                  }}
                  href='https://app.response.deloitte.com/e/er?s=958345745&lid=278068&elqTrackId=dc771f6f18924ef388a38b1029c11806&elq=00000000000000000000000000000000&elqaid=946&elqat=2'
                >
                  Get Started On Your GenAI Roadmap - ddbiz@deloitte.com
                </Link>
              </Box>
            </>
          )}
        </Container>
      </div>
    </>
  );
};

export default GenAiReadiness;
